/**
 * Square Menu Service for Festival Map Integration
 * Handles fetching and displaying vendor menus from Square POS
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Cache configuration for vendor menus
 */
const CACHE_KEY_PREFIX = 'vendor_menu_cache_';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Get cached menu data for a specific vendor
 */
const getCachedMenu = (vendorId) => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${vendorId}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    if (now - timestamp < CACHE_DURATION) {
      console.log(`📱 Using cached menu data for vendor ${vendorId}`);
      return data;
    }
    
    localStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.error('Error reading menu cache:', error);
    return null;
  }
};

/**
 * Cache menu data for a specific vendor
 */
const setCachedMenu = (vendorId, data) => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${vendorId}`;
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`💾 Cached menu data for vendor ${vendorId}`);
  } catch (error) {
    console.error('Error caching menu data:', error);
  }
};

/**
 * Fetch menu data for a specific vendor
 */
export const fetchVendorMenu = async (vendorId) => {
  try {
    console.log(`🔄 Fetching menu for vendor: ${vendorId}`);
    console.log(`🔗 API_BASE_URL: ${API_BASE_URL}`);
    console.log(`🔗 Full URL: ${API_BASE_URL}/api/square/menu/${vendorId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/square/menu/${vendorId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch menu');
    }

    console.log(`✅ Menu fetched successfully for vendor ${vendorId}:`, result.data);
    return result.data;

  } catch (error) {
    console.error(`❌ Error fetching menu for vendor ${vendorId}:`, error);
    throw new Error(`Failed to fetch menu: ${error.message}`);
  }
};

/**
 * Get menu data for a vendor (with caching)
 */
export const getVendorMenu = async (vendorId) => {
  try {
    // Check cache first
    const cachedData = getCachedMenu(vendorId);
    if (cachedData) {
      return cachedData;
    }

    // Fetch fresh data
    const menuData = await fetchVendorMenu(vendorId);
    
    // Cache the result
    setCachedMenu(vendorId, menuData);
    
    return menuData;

  } catch (error) {
    console.error(`Failed to get menu for vendor ${vendorId}:`, error);
    throw error;
  }
};

/**
 * Create an order through Square for a vendor
 */
export const createVendorOrder = async (vendorId, orderData) => {
  try {
    console.log(`🛒 Creating order for vendor ${vendorId}:`, orderData);
    
    const response = await fetch(`${API_BASE_URL}/api/square/order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vendorId,
        items: orderData.items,
        customerInfo: orderData.customerInfo,
        paymentMethod: orderData.paymentMethod
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to create order');
    }

    console.log(`✅ Order created successfully for vendor ${vendorId}:`, result);
    return result;

  } catch (error) {
    console.error(`❌ Error creating order for vendor ${vendorId}:`, error);
    throw new Error(`Failed to create order: ${error.message}`);
  }
};

/**
 * Get vendor information
 */
export const getVendorInfo = async (vendorId) => {
  try {
    console.log(`ℹ️ Fetching vendor info for: ${vendorId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/square/vendor/${vendorId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch vendor info');
    }

    console.log(`✅ Vendor info fetched successfully:`, result.vendor);
    return result.vendor;

  } catch (error) {
    console.error(`❌ Error fetching vendor info for ${vendorId}:`, error);
    throw new Error(`Failed to fetch vendor info: ${error.message}`);
  }
};

/**
 * Get all connected vendors
 */
export const getConnectedVendors = async () => {
  try {
    console.log(`🏪 Fetching connected vendors...`);
    
    const response = await fetch(`${API_BASE_URL}/api/square/vendors`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch vendors');
    }

    console.log(`✅ Connected vendors fetched successfully:`, result.vendors);
    return result.vendors;

  } catch (error) {
    console.error(`❌ Error fetching connected vendors:`, error);
    throw new Error(`Failed to fetch vendors: ${error.message}`);
  }
};

/**
 * Clear menu cache for a vendor
 */
export const clearMenuCache = (vendorId) => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${vendorId}`;
    localStorage.removeItem(cacheKey);
    console.log(`🗑️ Cleared menu cache for vendor ${vendorId}`);
  } catch (error) {
    console.error('Error clearing menu cache:', error);
  }
};

/**
 * Get popular items from a vendor's menu
 */
export const getPopularItems = (menuData, count = 5) => {
  if (!menuData || !menuData.menu) return [];
  
  const allItems = Object.values(menuData.menu).flat();
  return allItems.slice(0, count);
};

/**
 * Search menu items
 */
export const searchMenuItems = (menuData, query) => {
  if (!menuData || !menuData.menu || !query) return [];
  
  const searchTerm = query.toLowerCase();
  const allItems = Object.values(menuData.menu).flat();
  
  return allItems.filter(item => 
    item.name.toLowerCase().includes(searchTerm) ||
    item.description.toLowerCase().includes(searchTerm)
  );
};

export default {
  getVendorMenu,
  fetchVendorMenu,
  createVendorOrder,
  getVendorInfo,
  getConnectedVendors,
  clearMenuCache,
  getPopularItems,
  searchMenuItems
}; 